import React from 'react'
import { CircularProgress } from '@material-ui/core'
import { ConfirmDialog, Div } from '@sendoutcards/quantum-design-ui'

// @src imports
import { Confirm } from 'src/chrome'
import {
  useActions,
  useEffect,
  useMutations,
  useQueries,
  useState,
  useSubscriptions,
} from 'src/hooks'
import {
  getCard,
  getFairUseMarketingContent,
  getProductionInfo,
} from 'src/legacy_graphql'
import { CardPanelView } from 'src/orders/components'
import suspenseBoundary from '../../../../chrome/SuspenseBoundary/suspenseBoundaryHOC'
import styles from './sentCard.module.scss'
import { CardHistoryResendCardRoute } from 'src/dashboard/routes/CardHistoryRoute'
import { Memoized } from 'src/hooks/dependencies'

interface ViewSentCardProps {
  onClose: () => void
  sentOrderId: string | undefined
  sentContactId: string | undefined
}

interface ContentProps {
  sentOrderId: string | undefined
  sentContactId: string | undefined
  cardId: string
  setCardId: Memoized<(id: string) => void>
}

const Content: React.FC<ContentProps> = props => {
  const { sentOrderId, sentContactId, cardId, setCardId } = props
  const [productionInfo, card] = useQueries(
    sentOrderId && sentContactId
      ? getProductionInfo({ orderId: sentOrderId, contactId: sentContactId })
      : undefined,
    cardId ? getCard({ id: cardId }) : undefined,
  )

  useEffect(() => {
    if (productionInfo?.recipient?.card) {
      setCardId(productionInfo.recipient.card.id)
    }
  }, [productionInfo, setCardId])

  return (
    <>
      {card && (
        <Div style={{ position: 'relative', width: '100%' }}>
          <CardPanelView card={card} />
        </Div>
      )}
    </>
  )
}

const ViewSentCardContent = suspenseBoundary({
  component: Content,
  unresolved: <CircularProgress />,
  failure: error => `Failed loading notifications: ${error}`,
})

const ViewSentCard: React.FC<ViewSentCardProps> = props => {
  const { onClose, sentOrderId, sentContactId } = props
  const [marketingContent] = useQueries(getFairUseMarketingContent())
  const [cardId, setCardId] = useState('')
  const [isPopupOpen, setIsPopupOpen] = useState(false)
  const { shouldDisplaySystemSendWarning } = useSubscriptions()
  const mutations = useMutations()
  const actions = useActions()

  const onResendCard = async () => {
    try {
      const result = await mutations.createCard({
        card: cardId,
        isResend: true,
      })
      if (result.createCard.card) {
        actions.openCardHistory(
          undefined,
          undefined,
          CardHistoryResendCardRoute(result.createCard.card.id),
        )
      }
    } catch (error) {
      console.error(error)
    }
  }

  return (
    <Div>
      <Confirm
        title={'View your card'}
        onDecline={() => onClose()}
        childType={'image'}
        isModalEnabled={true}
        confirmTitle={cardId ? 'Resend' : undefined}
        onConfirm={
          cardId
            ? () => {
                if (shouldDisplaySystemSendWarning) {
                  setIsPopupOpen(true)
                } else {
                  onResendCard()
                }
              }
            : () => {}
        }
        wrapperClassName={styles.confirmWrapper}
        actionsClassName={styles.confirmActions}
        confirmMessageClassName={styles.confirmMessage}
      >
        <ViewSentCardContent
          sentOrderId={sentOrderId}
          sentContactId={sentContactId}
          cardId={cardId}
          setCardId={setCardId}
        />
      </Confirm>
      {isPopupOpen && (
        <ConfirmDialog
          isOpen={isPopupOpen}
          title=""
          description="Resending this heartfelt card will convert it to a system send, changing the price of the card. If you intended to send a heartfelt card, please go to the Card Catalog and create a new card that is unique to the person you are sending a card to."
          additionalLink={{
            title: 'Fair Use Policy',
            onClick: () => {
              window.open(marketingContent.fairUsePolicyLink.url)
            },
          }}
          decline={{
            title: 'Go Back',
            onClick: () => {
              setIsPopupOpen(false)
            },
          }}
          accept={{
            title: 'Continue with System Send',
            onClick: () => {
              setIsPopupOpen(false)
              onResendCard()
            },
          }}
          zIndex={1}
          data-mktcontent="link:fairUsePolicyLink"
        />
      )}
    </Div>
  )
}

export default ViewSentCard
